/**
 * Main application component with routing and layout.
 */

import { useEffect, useState } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import { Spin, Layout, Menu, Avatar, Dropdown, Button, Empty, Tooltip } from 'antd';
import {
  UserOutlined,
  LogoutOutlined,
  AppstoreOutlined,
  SettingOutlined,
  TeamOutlined,
  HomeOutlined,
  FileTextOutlined,
  MessageOutlined,
  KeyOutlined,
  FolderOutlined,
  BranchesOutlined,
  ToolOutlined,
  LockOutlined,
  UnlockOutlined,
  EditOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';

import { useAuthStore } from '@/store/authStore';
import { useConfigStore } from '@/store/configStore';
import { getAccessToken } from '@/api/client';
import { backgroundProbeObs } from '@/services/obsService';
import { useResizableSidebar } from '@/hooks/useResizableSidebar';
import Login from '@/pages/Login';
import Dashboard from '@/pages/Dashboard';
import PackageManagement from '@/pages/PackageManagement';
import PackageDetailPage from '@/pages/PackageDetailPage';
import ExternalReleaseManagement from '@/pages/ExternalReleaseManagement';
import ExternalReleaseDetailPage from '@/pages/ExternalReleaseDetailPage';
import SystemConfig from '@/pages/SystemConfig';
import PluginConfig from '@/pages/PluginConfig';
import UserManagement from '@/pages/UserManagement';
import RequirementManagement from '@/pages/RequirementManagement';
import DesignMaterialManagement from '@/pages/DesignMaterialManagement';
import OplManagement from '@/pages/opl';
import BcrTestPage from '@/pages/BcrTestPage';
import BcrArtifactsPage from '@/pages/BcrArtifactsPage';
import MainlineRequirements from '@/pages/MainlineRequirements';
import MainlineDesignDocs from '@/pages/MainlineDesignDocs';
import ProjectSpaceList from '@/pages/ProjectSpaceList';
import RequirementSync from '@/pages/RequirementSync';
import RequirementFlowback from '@/pages/RequirementFlowback';
import { listMainlineProjects } from '@/api/requirements';
import type { MainlineProject, Project } from '@/types';
import NotFound from '@/pages/NotFound';
import EnvBanner from '@/components/EnvBanner';
import FeedbackModal from '@/components/FeedbackModal';
import ProjectFormModal from '@/components/ProjectFormModal';
import ApiKeyModal from '@/components/ApiKeyModal';
import ApiDocModal from '@/components/ApiDocModal';
import InternalApiDocModal from '@/components/InternalApiDocModal';
import { uploadWarn } from '@/utils/uploadLogger';

const { Header, Sider, Content } = Layout;

// Protected route wrapper
const ProtectedRoute = () => {
  const location = useLocation();
  const { isAuthenticated, isLoading, fetchUser } = useAuthStore();
  const [hasCheckedToken, setHasCheckedToken] = useState(false);

  useEffect(() => {
    const token = getAccessToken();

    // 只在有 token 但未认证时才调用 fetchUser
    if (token && !isAuthenticated && !hasCheckedToken) {
      fetchUser().finally(() => {
        setHasCheckedToken(true);
      });
    } else if (!token) {
      // 没有 token，标记为已检查
      setHasCheckedToken(true);
    } else if (isAuthenticated) {
      // 已认证，标记为已检查
      setHasCheckedToken(true);
    }
  }, [isAuthenticated, fetchUser, hasCheckedToken]);

  const token = getAccessToken();

  // 没有 token，直接跳转登录页
  if (!token) {
    const redirectPath = location.pathname + location.search;
    return <Navigate to={`/login?redirect=${encodeURIComponent(redirectPath)}`} replace />;
  }

  // 正在加载或还未完成 token 检查，显示加载状态
  if (isLoading || !hasCheckedToken) {
    return <Spin size="large" description="加载中..." fullscreen />;
  }

  // Token 已检查，但验证失败，跳转登录页
  if (!isAuthenticated) {
    const redirectPath = location.pathname + location.search;
    return <Navigate to={`/login?redirect=${encodeURIComponent(redirectPath)}`} replace />;
  }

  return <AppLayout />;
};

// Main application layout
const AppLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const { projects, fetchProjects, mountains, fetchMountains, projectsLoading } = useConfigStore();
  const [collapsed, setCollapsed] = useState(false);
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [apiKeyModalOpen, setApiKeyModalOpen] = useState(false);
  const [apiDocModalOpen, setApiDocModalOpen] = useState(false);
  const [internalApiDocModalOpen, setInternalApiDocModalOpen] = useState(false);
  const [mainlineProjects, setMainlineProjects] = useState<MainlineProject[]>([]);
  const [lockedProjectId, setLockedProjectId] = useState<number | null>(null);
  const { width: sidebarWidth, isResizing, handleMouseDown } = useResizableSidebar();
  const [lockedMountainName, setLockedMountainName] = useState<string | null>(null);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);

  useEffect(() => {
    fetchProjects();
    fetchMountains();
  }, [fetchProjects, fetchMountains]);

  useEffect(() => {
    const refresh = () => {
      listMainlineProjects().then((res) => {
        if (res.code === 200) setMainlineProjects(res.data);
      }).catch(() => {});
    };
    refresh();
    window.addEventListener('mainline-project-changed', refresh);
    return () => window.removeEventListener('mainline-project-changed', refresh);
  }, []);

  // 探测 OBS 连通性：结果仅用于上传前的诊断提示，首屏不依赖它，
  // 因此推迟到浏览器空闲再发起，避免与菜单/项目列表争抢连接与后端处理能力。
  useEffect(() => {
    const run = () => {
      backgroundProbeObs().catch((err) => {
        uploadWarn('[App] Background OBS probe failed:', err);
      });
    };

    type IdleWindow = Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (handle: number) => void;
    };
    const w = window as IdleWindow;

    if (typeof w.requestIdleCallback === 'function') {
      const handle = w.requestIdleCallback(run, { timeout: 5000 });
      return () => w.cancelIdleCallback?.(handle);
    }
    // Safari 等不支持 requestIdleCallback 的浏览器退化为延时
    const timer = window.setTimeout(run, 2000);
    return () => window.clearTimeout(timer);
  }, []);

  // 统一使用 configStore.projects：后端按用户权限过滤（内部用户全量，外部用户仅授权项目），
  // 且已通过 _fetch_bvp_mountains 注入 BVP 山头数据，外部用户登录信息中的 permitted_projects
  // 不包含 mountain 字段，会导致 BVP-linked 项目的山头显示为"未分组"
  const visibleProjects = projects;

  // 当前路由指向的项目 id：列表型路由取路径第二段，详情型路由取 ?projectId=
  const getRouteProjectId = (): string | null => {
    const path = location.pathname;
    if (
      ['/packages/', '/requirements/', '/design-materials/', '/opl/', '/external-releases/'].some(
        (prefix) => path.startsWith(prefix)
      )
    ) {
      return path.split('/')[2] ?? null;
    }
    if (path.startsWith('/package/') || path.startsWith('/external-release/')) {
      return new URLSearchParams(location.search).get('projectId');
    }
    return null;
  };

  // Determine selected menu key based on current route
  const getSelectedKey = () => {
    const path = location.pathname;
    if (path === '/mainline-requirements') return 'mainline';
    if (path === '/project-space-list') return 'bsp';
    if (path === '/mainline-design-docs') return 'mainline-design-docs';
    if (path.startsWith('/mainline-project-requirements/')) {
      const pid = path.split('/')[2];
      return `mainline-project-${pid}`;
    }
    if (path === '/') return 'home';
    if (path === '/config') return 'config';
    if (path === '/plugins') return 'plugins';
    if (path === '/users') return 'users';
    if (path === '/requirement-sync') return 'requirement-sync';
    if (path === '/requirement-flowback') return 'requirement-flowback';
    if (path === '/packages') return 'home';

    const projectId = getRouteProjectId();
    if (!projectId) return '';
    if (path.startsWith('/packages/')) return `pkg-${projectId}`;
    if (path.startsWith('/requirements/')) return `req-${projectId}`;
    if (path.startsWith('/design-materials/')) return `dm-${projectId}`;
    if (path.startsWith('/opl/')) return `opl-${projectId}`;
    if (path.startsWith('/external-releases/')) return `er-${projectId}`;
    if (path.startsWith('/package/')) return `pkg-${projectId}`;
    if (path.startsWith('/external-release/')) return `er-${projectId}`;
    return '';
  };

  // Determine which project sub-menus to open based on current route
  const getOpenKeys = () => {
    const path = location.pathname;

    if (path === '/mainline-requirements' || path === '/mainline-design-docs') {
      return ['mainline'];
    }
    if (path === '/project-space-list') {
      return ['bsp'];
    }
    if (path === '/requirement-sync' || path === '/requirement-flowback') {
      return ['tools'];
    }
    if (path.startsWith('/mainline-project-requirements/')) {
      return ['mainline', 'mainline-requirements'];
    }

    const projectId = getRouteProjectId();
    if (!projectId) return ['bsp'];

    const project = visibleProjects.find((p) => p.id === Number(projectId));
    const mountainName = project?.mountain?.name;
    if (mountainName) {
      return ['bsp', `mountain-${mountainName}`, `project-${projectId}`];
    }
    if (project) {
      return ['bsp', 'mountain-ungrouped', `project-${projectId}`];
    }
    return ['bsp', `project-${projectId}`];
  };

  const [openKeys, setOpenKeys] = useState<string[]>(() => getOpenKeys());
  useEffect(() => {
    // 项目列表异步到达后必须重算：直接打开分享链接（如 /external-release/201?projectId=10109）
    // 时首帧 projects 为空，无法由 projectId 反查山头，只依赖 pathname/search 会永远停在山头折叠态
    const routeProjectId = getRouteProjectId();
    const target = routeProjectId
      ? visibleProjects.find((p) => p.id === Number(routeProjectId))
      : undefined;

    // 路由切到别的项目时解除侧边栏锁定，否则目标项目被锁定过滤掉、展开也无从显示
    if (target) {
      setLockedProjectId((prev) => (prev !== null && prev !== target.id ? null : prev));
      setLockedMountainName((prev) =>
        prev !== null && prev !== target.mountain?.name ? null : prev
      );
    }

    setOpenKeys(getOpenKeys());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search, visibleProjects]);

  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    if (key === 'home') {
      navigate('/');
    } else if (key === 'config') {
      navigate('/config');
    } else if (key === 'plugins') {
      navigate('/plugins');
    } else if (key === 'users') {
      navigate('/users');
    } else if (key === 'api-doc') {
      setInternalApiDocModalOpen(true);
    } else if (key.startsWith('pkg-')) {
      const projectId = key.replace('pkg-', '');
      navigate(`/packages/${projectId}`);
    } else if (key.startsWith('req-')) {
      const reqProjectId = key.replace('req-', '');
      navigate(`/requirements/${reqProjectId}`);
    } else if (key.startsWith('dm-')) {
      const dmProjectId = key.replace('dm-', '');
      navigate(`/design-materials/${dmProjectId}`);
    } else if (key.startsWith('opl-')) {
      const oplProjectId = key.replace('opl-', '');
      navigate(`/opl/${oplProjectId}`);
    } else if (key.startsWith('er-')) {
      const erProjectId = key.replace('er-', '');
      navigate(`/external-releases/${erProjectId}`);
    } else if (key === 'mainline-design-docs') {
      navigate('/mainline-design-docs');
    } else if (key === 'requirement-sync') {
      navigate('/requirement-sync');
    } else if (key === 'requirement-flowback') {
      navigate('/requirement-flowback');
    } else if (key.startsWith('mainline-project-')) {
      const projectId = key.replace('mainline-project-', '');
      navigate(`/mainline-project-requirements/${projectId}`);
    }
  };

  // Build project submenu children (shared helper)
  const buildProjectChildren = (project: typeof visibleProjects[0]) => {
    // Show "需求" unless the project is configured to not create requirements
    const showRequirements = project.req_mode !== 'none';
    return [
      ...(project.allowed_file_types?.length ? [{ key: `pkg-${project.id}`, label: '底软交付物' }] : []),
      ...(showRequirements ? [{ key: `req-${project.id}`, label: '需求' }] : []),
      ...(project.design_material_required !== false
        ? [{ key: `dm-${project.id}`, label: '设计材料' }]
        : []),
      ...(project.opl_required !== false
        ? [{ key: `opl-${project.id}`, label: 'OPL' }]
        : []),
      ...(project.momenta_file_types?.length ? [{ key: `er-${project.id}`, label: 'Momenta释放产物包' }] : []),
    ];
  };

  // 直接从项目数据构建山头分组，不依赖 BSP mountains store（BVP 山头可能不在 BSP 列表里）
  const mountainGroups = (() => {
    const seen = new Map<string, { name: string; sortOrder: number }>();
    for (const p of visibleProjects) {
      if (p.mountain?.name && !seen.has(p.mountain.name)) {
        const bspMountain = mountains.find((m) => m.name === p.mountain!.name);
        seen.set(p.mountain.name, {
          name: p.mountain.name,
          sortOrder: bspMountain?.sort_order ?? 999,
        });
      }
    }
    return Array.from(seen.values()).sort(
      (a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name)
    );
  })();
  const buildProjectLabel = (project: (typeof visibleProjects)[0]) => {
    const isLocked = lockedProjectId === project.id;
    return (
      <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Tooltip title={project.name} placement="right">
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>{project.name}</span>
        </Tooltip>
        {user?.user_type === 'internal' && (
          <Tooltip title="编辑项目" placement="right">
            <span
              onClick={(e) => {
                e.stopPropagation();
                setEditingProject(project);
                setIsProjectModalOpen(true);
              }}
              style={{ marginLeft: 4, opacity: 0.5, flexShrink: 0, cursor: 'pointer' }}
            >
              <EditOutlined />
            </span>
          </Tooltip>
        )}
        <span
          onClick={(e) => {
            e.stopPropagation();
            setLockedMountainName(null);
            setLockedProjectId(isLocked ? null : project.id);
          }}
          style={{ marginLeft: 4, color: isLocked ? '#faad14' : undefined, opacity: isLocked ? 1 : 0.5, flexShrink: 0, cursor: 'pointer' }}
        >
          {isLocked ? <UnlockOutlined /> : <LockOutlined />}
        </span>
      </span>
    );
  };

  const buildMountainLabel = (mountainName: string) => {
    const isLocked = lockedMountainName === mountainName;
    return (
      <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>{mountainName}</span>
        <span
          onClick={(e) => {
            e.stopPropagation();
            setLockedProjectId(null);
            setLockedMountainName(isLocked ? null : mountainName);
          }}
          style={{ marginLeft: 4, color: isLocked ? '#faad14' : undefined, opacity: isLocked ? 1 : 0.5, flexShrink: 0 }}
        >
          {isLocked ? <UnlockOutlined /> : <LockOutlined />}
        </span>
      </span>
    );
  };

  const buildBspChildren = () => {
    const displayProjects =
      lockedProjectId !== null
        ? visibleProjects.filter((p) => p.id === lockedProjectId)
        : lockedMountainName !== null
          ? visibleProjects.filter((p) => p.mountain?.name === lockedMountainName)
          : visibleProjects;

    if (displayProjects.length === 0) {
      return [{ key: 'no-projects', label: '暂无可访问的项目', disabled: true }];
    }

    const items: NonNullable<MenuProps['items']> = [];

    // Mountain-grouped projects（用 name 作 key，避免 BVP/BSP ID 不一致）
    for (const mountain of mountainGroups) {
      const mountainProjects = displayProjects.filter((p) => p.mountain?.name === mountain.name);
      if (mountainProjects.length === 0) continue;
      items.push({
        key: `mountain-${mountain.name}`,
        icon: <FolderOutlined />,
        label: buildMountainLabel(mountain.name),
        children: mountainProjects.map((project) => ({
          key: `project-${project.id}`,
          label: buildProjectLabel(project),
          children: buildProjectChildren(project),
        })),
      });
    }

    // 未分组项目（无山头）
    const displayUngrouped = displayProjects.filter((p) => !p.mountain);
    if (displayUngrouped.length > 0) {
      items.push({
        key: 'mountain-ungrouped',
        icon: <FolderOutlined />,
        label: '未分组',
        children: displayUngrouped.map((project) => ({
          key: `project-${project.id}`,
          label: buildProjectLabel(project),
          children: buildProjectChildren(project),
        })),
      });
    }

    return items;
  };

  const isExternal = user?.user_type === 'external';

  const menuItems: MenuProps['items'] = [
    {
      key: 'home',
      icon: <HomeOutlined />,
      label: '首页',
    },
    ...(!isExternal ? [{
      key: 'mainline',
      icon: <BranchesOutlined />,
      label: <span onClick={() => navigate('/mainline-requirements')}>主线空间</span>,
      children: [
        {
          key: 'mainline-requirements',
          label: '主线需求',
          children: mainlineProjects.length > 0
            ? mainlineProjects.map((p) => ({
                key: `mainline-project-${p.id}`,
                label: <Tooltip title={p.project} placement="right">{p.project}</Tooltip>,
              }))
            : [{ key: 'no-mainline-projects', label: '暂无主线项目', disabled: true }],
        },
        { key: 'mainline-design-docs', label: '主线设计文档' },
      ],
    }] : []),
    {
      key: 'bsp',
      icon: <AppstoreOutlined />,
      label: <span onClick={() => navigate('/project-space-list')}>项目空间</span>,
      children: buildBspChildren(),
    },
    ...(!isExternal ? [{
      key: 'tools',
      icon: <ToolOutlined />,
      label: '工具',
      children: [
        { key: 'requirement-sync', label: '需求同步' },
        { key: 'requirement-flowback', label: '需求回流' },
      ],
    }] : []),
  ];

  // Admin-only menu items
  if (user?.is_admin) {
    menuItems.push(
      {
        key: 'config',
        icon: <SettingOutlined />,
        label: '系统配置',
      },
      {
        key: 'users',
        icon: <TeamOutlined />,
        label: '用户管理',
      },
      {
        key: 'plugins',
        icon: <SettingOutlined />,
        label: '插件配置',
      }
    );
  }

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: (
        <span>
          {user?.display_name || user?.username}
          <br />
          <small className="text-gray-400">
            {user?.user_type === 'internal' ? '内部用户' : '外部用户'}
          </small>
        </span>
      ),
      disabled: true,
    },
    {
      type: 'divider',
    },
    {
      key: 'get-key',
      icon: <KeyOutlined />,
      label: '获取 API Key',
      onClick: () => setApiKeyModalOpen(true),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      danger: true,
      onClick: () => {
        logout();
        window.location.href = '/login';
      },
    },
  ];

  // 外层 h-screen + overflow-hidden：把滚动限制在内部区域，
  // 使侧边栏与正文各自滚动，页面级（body）不再产生公共滚动条
  return (
    <Layout className="h-screen overflow-hidden">
      <EnvBanner />
      <Header className="flex items-center justify-between px-6 bg-white shadow-sm border-b">
        <div className="text-xl font-semibold text-blue-600">
          BCR管理系统
        </div>
        <div className="flex items-center gap-1">
          <Button
            type="text"
            icon={<FileTextOutlined />}
            onClick={() => {
              const hostname = window.location.hostname;
              if (hostname.endsWith('.works')) {
                setInternalApiDocModalOpen(true);
              } else {
                setApiDocModalOpen(true);
              }
            }}
            title="查看 API 文档"
            className="text-gray-600 hover:text-blue-600"
          >
            API DOC
          </Button>
          <Button
            type="text"
            icon={<MessageOutlined />}
            onClick={() => setFeedbackModalOpen(true)}
            title="提交反馈"
            className="text-gray-600 hover:text-blue-600"
          >
            FeedBack
          </Button>
          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
            <Button type="text" className="flex items-center h-auto py-1">
              <Avatar
                size="small"
                icon={<UserOutlined />}
                className="mr-2 bg-blue-500"
              />
              <span className="max-w-32 truncate">
                {user?.display_name || user?.username}
              </span>
            </Button>
          </Dropdown>
        </div>
      </Header>
      <Layout className="flex-1 min-h-0">
        <Sider
          width={collapsed ? 80 : sidebarWidth}
          theme="dark"
          collapsible
          collapsed={collapsed}
          onCollapse={setCollapsed}
          className="shadow-md relative"
          style={{ position: 'relative', height: '100%' }}
        >
          <div
            className="sidebar-resize-handle"
            onMouseDown={handleMouseDown}
            style={{
              position: 'absolute',
              right: 0,
              top: 0,
              width: '4px',
              height: '100%',
              cursor: 'col-resize',
              background: isResizing ? '#177ddc' : 'transparent',
            }}
          />
          {/* 菜单单独滚动；resize handle 留在外层，不随菜单一起滚动。
              减去 48px 为 Sider 底部折叠触发器让位。 */}
          <div className="bsp-sidebar-scroll h-[calc(100%-48px)] overflow-y-auto overflow-x-hidden">
            {projectsLoading && projects.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <Spin size="small" />
              </div>
            ) : (
              <Menu
                mode="inline"
                theme="dark"
                selectedKeys={[getSelectedKey()]}
                openKeys={openKeys}
                onOpenChange={(keys) => setOpenKeys(keys)}
                items={menuItems}
                onClick={handleMenuClick}
                inlineIndent={12}
                className="bsp-sidebar-menu"
              />
            )}
          </div>
        </Sider>
        <Content className="p-6 bg-gray-50 overflow-auto min-h-0">
          {user?.user_type === 'external' && visibleProjects.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <Empty
                description="暂无可访问的项目，请联系管理员分配权限"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            </div>
          ) : (
            <Outlet />
          )}
        </Content>
      </Layout>
      <FeedbackModal
        open={feedbackModalOpen}
        onClose={() => setFeedbackModalOpen(false)}
      />
      <ProjectFormModal
        open={isProjectModalOpen}
        project={editingProject}
        defaultMainline={false}
        onSuccess={() => {
          setIsProjectModalOpen(false);
          setEditingProject(null);
          fetchProjects(true);
        }}
        onCancel={() => {
          setIsProjectModalOpen(false);
          setEditingProject(null);
        }}
      />
      <ApiKeyModal
        open={apiKeyModalOpen}
        onClose={() => setApiKeyModalOpen(false)}
      />
      <ApiDocModal
        open={apiDocModalOpen}
        onClose={() => setApiDocModalOpen(false)}
      />
      <InternalApiDocModal
        open={internalApiDocModalOpen}
        onClose={() => setInternalApiDocModalOpen(false)}
      />
    </Layout>
  );
};

// Main App component
const App = () => {
  return (
    <BrowserRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route element={<ProtectedRoute />}>
          <Route index element={<Dashboard />} />
          <Route path="packages" element={<PackageManagement />} />
          <Route path="packages/:projectId" element={<PackageManagement />} />
          <Route path="package/:packageId" element={<PackageDetailPage />} />
          <Route path="config" element={<SystemConfig />} />
          <Route path="plugins" element={<PluginConfig />} />
          <Route path="users" element={<UserManagement />} />
          <Route path="requirements/:projectId" element={<RequirementManagement />} />
          <Route path="design-materials/:projectId" element={<DesignMaterialManagement />} />
          <Route path="opl/:projectId" element={<OplManagement />} />
          <Route path="external-releases/:projectId" element={<ExternalReleaseManagement />} />
          <Route path="external-release/:releaseId" element={<ExternalReleaseDetailPage />} />
          <Route path="packages/:projectId/bcr/:packageId" element={<BcrTestPage />} />
          <Route path="packages/:projectId/artifacts/:packageId" element={<BcrArtifactsPage />} />
          <Route path="mainline-requirements" element={<MainlineRequirements />} />
          <Route path="project-space-list" element={<ProjectSpaceList />} />
          <Route path="mainline-design-docs" element={<MainlineDesignDocs />} />
          <Route path="mainline-project-requirements/:projectId" element={<RequirementManagement />} />
          <Route path="requirement-sync" element={<RequirementSync />} />
          <Route path="requirement-flowback" element={<RequirementFlowback />} />
        </Route>
        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  );
};

export default App;
